import {
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { bool, func, object } from "prop-types";
import { useSnackbar } from "notistack";
import { useAddSubscription, useUpdateSubscription } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { KeyTextField } from "@/components";

type AddSubscriptionProps = {
  open?: boolean;
  handleClose: (open: boolean) => void;
  row: {
    id?: string | number | null;
    key?: string | null;
    name?: string | null;
  };
};

export const AddSubscription = (props: AddSubscriptionProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    open: false,
  };

  const [inputSubscription, setInputSubscription] = useState("");
  const [inputKey, setInputKey] = useState("");

  // Use props.open directly instead of maintaining state
  const open = props.open ?? propsDefault.open;

  useEffect(() => {
    if (props.row.key) {
      // Use setTimeout to defer state updates and avoid cascading renders
      setTimeout(() => {
        setInputSubscription(props.row.name);
        setInputKey(props.row.key);
      }, 0);
    } else {
      setTimeout(() => {
        setInputSubscription("");
        setInputKey("");
      }, 0);
    }
  }, [props.row]);

  const handleClose = () => {
    props.handleClose(false);
  };

  const resetForm = useCallback(() => {
    setInputKey("");
    setInputSubscription("");
  }, []);

  const { addSubscriptionPlan, loading: updatingAdd } = useAddSubscription(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("Subscription Plan added Successfully"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateSubscriptionPlan, loading: updatingEdit } =
    useUpdateSubscription(
      () => {
        resetForm();
        handleClose();
        enqueueSnackbar(gettext("Subscription updated Successfully"), {
          variant: "success",
        });
      },
      (error) => {
        if (error === "409") {
          // setEmailError(true);
        } else if (error === "410") {
          enqueueSnackbar(
            gettext("You are not authorized to perform this action"),
            {
              variant: "error",
            },
          );
        } else {
          enqueueSnackbar(gettext("Error: Something went wrong!"), {
            variant: "error",
          });
        }
      },
    );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables: any = {
      key: inputKey,
      name: inputSubscription,
    };

    if (props.row.id) {
      variables.subscriptionPlanId = props.row.id;
      updateSubscriptionPlan({
        variables: variables,
        refetchQueries: ["GetSubscriptionPlans"],
      }).then();
    } else {
      addSubscriptionPlan({
        variables: variables,
        refetchQueries: ["GetSubscriptionPlans"],
      }).then();
    }
  };

  const handleSubscriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setInputSubscription(event.target.value);
  };

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputKey(event.target.value);
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 450,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.key && gettext("Add New Subscription")}
              {props.row.key && gettext("Edit Subscription")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="text"
                    label={gettext("Subscription Plan Name")}
                    placeholder={gettext("Subscription Plan Name")}
                    size="small"
                    required
                    fullWidth
                    value={inputSubscription}
                    onChange={handleSubscriptionChange}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <KeyTextField
                    type="text"
                    label={gettext("Key")}
                    placeholder={gettext(
                      "Subscription key, Alpha numeric and dash only",
                    )}
                    size="small"
                    required
                    fullWidth
                    value={inputKey}
                    onChange={handleKeyChange}
                    disabled={!!props.row.key}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={updatingAdd || updatingEdit}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

AddSubscription.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
